import type { ParentToMiniappNotificationInputOf } from "schemas/miniappRpc";

export type MiniappRegistryShareRecord =
  ParentToMiniappNotificationInputOf<"registryShareUpdated">;
export type MiniappRegistryShareResult =
  ParentToMiniappNotificationInputOf<"registryShareResult">;

export type MiniappRegistryShareSourceIdentity = {
  type: MiniappRegistryShareRecord["type"];
  sourceType: string;
  sourceKey: string;
};

export type InFlightShareRequest = MiniappRegistryShareSourceIdentity & {
  startedAt: number;
};
export type ShareFailure = MiniappRegistryShareSourceIdentity & {
  failedAt: number;
  // Real message from the editor's pushed failure; null/absent means the
  // backstop timeout fired, so the view uses the generic timeout copy.
  message?: string | null;
};

// Backstop for a share whose RPC never pushes any result (e.g. dropped socket).
// Success/failure surface immediately via pushes, so this just needs to outlast
// the 180s exec budget + sandbox cold-start to avoid a false "took too long".
export const REGISTRY_SHARE_REQUEST_TIMEOUT_MS = 210_000;
export const SHARE_RECORD_POLL_INTERVAL_MS = 2_500;
const SHARE_TIMEOUT_MESSAGE =
  "The share link took too long to finish. Try again.";

export function registryShareRecordQueryKey(
  sourceIdentity: MiniappRegistryShareSourceIdentity,
) {
  return ["miniappRegistryShareRecord", sourceIdentity] as const;
}

// Companion to the record key: the editor pushes a failed share here so
// `useRegistryShare` surfaces the real error without waiting out the backstop.
export function registryShareFailureQueryKey(
  sourceIdentity: MiniappRegistryShareSourceIdentity,
) {
  return ["miniappRegistryShareFailure", sourceIdentity] as const;
}

export function registryShareSourceKeyFromWorkspaceFilePath({
  entrypointPath,
}: {
  entrypointPath: string;
}): string {
  return entrypointPath.replace(/^\/workspace\/?/u, "").replace(/^\/+/u, "");
}

function isSameShareSource(
  candidate: MiniappRegistryShareSourceIdentity,
  sourceIdentity: MiniappRegistryShareSourceIdentity,
): boolean {
  return (
    candidate.type === sourceIdentity.type &&
    candidate.sourceType === sourceIdentity.sourceType &&
    candidate.sourceKey === sourceIdentity.sourceKey
  );
}

function isRecordFresh(
  shareRecord: MiniappRegistryShareRecord | null,
  startedAt: number,
): boolean {
  return shareRecord !== null && Date.parse(shareRecord.updatedAt) >= startedAt;
}

/**
 * Pure view derivation: `isSharing` and `registryShareResult` for one source,
 * given the in-flight request, the last failure, and the current record. The
 * request/failure only count when they match `sourceIdentity` — surfaces that
 * reuse one hook instance across sources (Plans, Branding) would otherwise
 * leak in-flight state onto the next source (stuck spinner / false timeout).
 */
export function deriveRegistryShareView({
  sourceIdentity,
  shareRequest,
  shareFailure,
  shareRecord,
}: {
  sourceIdentity: MiniappRegistryShareSourceIdentity;
  shareRequest: InFlightShareRequest | null;
  shareFailure: ShareFailure | null;
  shareRecord: MiniappRegistryShareRecord | null;
}): {
  isSharing: boolean;
  registryShareResult: MiniappRegistryShareResult | null;
} {
  const activeRequest =
    shareRequest !== null && isSameShareSource(shareRequest, sourceIdentity)
      ? shareRequest
      : null;
  // NOTE (Ezra, 2026-07-01, REPL-28638): a failure only counts while its
  // request is in flight — the pushed failure outlives the popover in the
  // query cache and must not outrank a still-valid record after a remount.
  const activeFailure =
    activeRequest !== null &&
    shareFailure !== null &&
    isSameShareSource(shareFailure, sourceIdentity)
      ? shareFailure
      : null;
  const shareResolved =
    activeRequest !== null &&
    isRecordFresh(shareRecord, activeRequest.startedAt);
  const isSharing =
    activeRequest !== null && activeFailure === null && !shareResolved;
  let registryShareResult: MiniappRegistryShareResult | null;
  if (activeFailure !== null && !shareResolved) {
    registryShareResult = {
      status: "failed",
      type: sourceIdentity.type,
      sourceType: sourceIdentity.sourceType,
      sourceKey: sourceIdentity.sourceKey,
      message: activeFailure.message ?? SHARE_TIMEOUT_MESSAGE,
      failedAt: new Date(activeFailure.failedAt).toISOString(),
    };
  } else if (shareRecord !== null) {
    registryShareResult = { status: "success", record: shareRecord };
  } else {
    registryShareResult = null;
  }
  return { isSharing, registryShareResult };
}

/**
 * Pure poll cadence for the record query: poll only while a matching share is in
 * flight, has not timed out, and no fresh record has landed yet. `false` stops
 * polling.
 */
export function registryShareRecordPollInterval({
  sourceIdentity,
  shareRequest,
  shareRecord,
  now,
}: {
  sourceIdentity: MiniappRegistryShareSourceIdentity;
  shareRequest: InFlightShareRequest | null;
  shareRecord: MiniappRegistryShareRecord | null;
  now: number;
}): number | false {
  if (
    shareRequest === null ||
    !isSameShareSource(shareRequest, sourceIdentity)
  ) {
    return false;
  }
  if (now - shareRequest.startedAt >= REGISTRY_SHARE_REQUEST_TIMEOUT_MS) {
    return false;
  }
  if (isRecordFresh(shareRecord, shareRequest.startedAt)) {
    return false;
  }
  return SHARE_RECORD_POLL_INTERVAL_MS;
}
